import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import Label from "@/components/Label";
import { useLanguage } from "@/context/LanguageContext";
import { apropos, mission } from "@/content/translations";

export default function AProposPage() {
    const { lang } = useLanguage();
    const t = apropos[lang];
    const m = mission[lang];
    const [formData, setFormData] = useState({ nom: "", email: "", message: "" });
    const [sent, setSent] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        window.location.href = `mailto:contact@timemetria.com?subject=Message de ${formData.nom}&body=${encodeURIComponent(formData.message)}%0A%0A${formData.email}`;
        setSent(true);
    };

    return (
        <>
            <Header />
            <main className="tm-page-in">
                <section className="max-w-[1360px] mx-auto px-8 sm:px-16 pt-24 sm:pt-32">
                    <Reveal className="max-w-3xl">
                        <Label>{t.label}</Label>
                        <h1 className="tm-serif text-[3.25rem] sm:text-[5rem] leading-[0.95] mt-6 text-[hsl(var(--tm-anthracite))]">
                            Timemetria
                        </h1>
                        <div className="mt-8 h-px w-28 bg-[hsl(var(--tm-gold))] tm-line-draw"></div>
                        <p className="mt-10 tm-serif text-[1.35rem] leading-relaxed text-[hsl(var(--tm-anthracite-soft))]">
                            {t.intro}
                        </p>
                    </Reveal>
                </section>

                <section className="max-w-[1360px] mx-auto px-8 sm:px-16 mt-24 sm:mt-32">
                    <Reveal className="tm-surface p-10 sm:p-14">
                        <Label>{m.label}</Label>
                        <h2 className="tm-serif text-2xl sm:text-3xl mt-4 text-[hsl(var(--tm-anthracite))] max-w-2xl">{m.title}</h2>
                        <div className="mt-8 space-y-5 max-w-3xl">
                            <p className="text-[hsl(var(--tm-anthracite-soft))] leading-relaxed">{m.text1}</p>
                            <p className="text-[hsl(var(--tm-anthracite-soft))] leading-relaxed">{m.text2}</p>
                            <p className="text-[hsl(var(--tm-anthracite-soft))] leading-relaxed">{m.text3}</p>
                        </div>
                    </Reveal>
                </section>

                <section className="max-w-[1360px] mx-auto px-8 sm:px-16 mt-28 sm:mt-36">
                    <div className="tm-divider mb-16"></div>
                    <Reveal>
                        <Label>{t.valuesLabel}</Label>
                        <h2 className="tm-serif text-3xl sm:text-4xl mt-4 text-[hsl(var(--tm-anthracite))]">{t.valuesTitle}</h2>
                    </Reveal>
                    <Reveal stagger className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-10">
                        {t.values.map((v) => (
                            <div key={v.title}>
                                <h3 className="tm-serif text-xl text-[hsl(var(--tm-anthracite))]">{v.title}</h3>
                                <div className="mt-4 h-px w-10 bg-[hsl(var(--tm-gold))]"></div>
                                <p className="mt-5 text-[hsl(var(--tm-anthracite-soft))] leading-relaxed">{v.text}</p>
                            </div>
                        ))}
                    </Reveal>
                </section>

                <section className="max-w-[1360px] mx-auto px-8 sm:px-16 mt-32 sm:mt-44">
                    <div className="tm-divider mb-16"></div>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
                        <Reveal className="lg:col-span-7">
                            <Label>{t.founderLabel}</Label>
                            <h2 className="tm-serif text-3xl sm:text-4xl mt-4 text-[hsl(var(--tm-anthracite))]">{t.founderName}</h2>
                            <p className="mt-8 text-[hsl(var(--tm-anthracite-soft))] leading-relaxed text-lg">{t.founderText1}</p>
                            <p className="mt-6 text-[hsl(var(--tm-anthracite-soft))] leading-relaxed text-lg">{t.founderText2}</p>
                            <div className="mt-10 tm-card-lg overflow-hidden max-w-md">
                                <img
                                    src="/images/rapport-expertise.jpg"
                                    alt="Watch resting on a velvet cushion beside a notebook and fountain pen"
                                    className="w-full h-[220px] object-cover"
                                    loading="lazy"
                                />
                            </div>
                        </Reveal>
                        <Reveal className="lg:col-span-4 lg:col-start-9" delay={0.1}>
                            <div className="tm-surface p-10">
                                <div className="tm-label">{t.expertiseBoxLabel}</div>
                                <div className="mt-6 h-px w-10 bg-[hsl(var(--tm-gold))]"></div>
                                <p className="mt-6 text-[hsl(var(--tm-anthracite-soft))] leading-relaxed">{t.expertiseBoxText}</p>
                                <div className="mt-8 pt-6 border-t border-[hsl(var(--tm-hairline))] flex flex-col gap-3">
                                    <Link to="/expertise" className="tm-label tm-gold-link">{t.expertiseBoxLink}</Link>
                                    <a href="mailto:contact@timemetria.com" className="tm-label tm-gold-link">contact@timemetria.com</a>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </section>

                <section className="max-w-[1360px] mx-auto px-8 sm:px-16 mt-32 sm:mt-44">
                    <div className="tm-divider mb-16"></div>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
                        <Reveal className="lg:col-span-4">
                            <Label>{t.contactLabel}</Label>
                            <h2 className="tm-serif text-3xl sm:text-4xl mt-4 text-[hsl(var(--tm-anthracite))]">{t.contactTitle}</h2>
                            <div className="mt-6 h-px w-10 bg-[hsl(var(--tm-gold))]"></div>
                            <p className="mt-8 text-[hsl(var(--tm-anthracite-soft))] leading-relaxed">{t.contactText}</p>
                            <a href="mailto:contact@timemetria.com" className="mt-6 inline-block tm-label tm-gold-link">contact@timemetria.com</a>
                        </Reveal>
                        <Reveal className="lg:col-span-7 lg:col-start-6" delay={0.1}>
                            {sent ? (
                                <div className="tm-surface p-10 text-center">
                                    <p className="tm-serif text-2xl text-[hsl(var(--tm-anthracite))]">{t.formSentTitle}</p>
                                    <p className="mt-4 text-[hsl(var(--tm-anthracite-soft))]">{t.formSentText}</p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    <div>
                                        <label className="tm-label block mb-2">{t.formName}</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.nom}
                                            onChange={e => setFormData({...formData, nom: e.target.value})}
                                            className="w-full px-5 py-4 bg-[hsl(var(--tm-ivory-deep))] border border-[hsl(var(--tm-hairline))] text-[hsl(var(--tm-anthracite))] tm-serif text-lg focus:outline-none focus:border-[hsl(var(--tm-gold))] transition-colors"
                                        />
                                    </div>
                                    <div>
                                        <label className="tm-label block mb-2">{t.formEmail}</label>
                                        <input
                                            type="email"
                                            required
                                            value={formData.email}
                                            onChange={e => setFormData({...formData, email: e.target.value})}
                                            className="w-full px-5 py-4 bg-[hsl(var(--tm-ivory-deep))] border border-[hsl(var(--tm-hairline))] text-[hsl(var(--tm-anthracite))] tm-serif text-lg focus:outline-none focus:border-[hsl(var(--tm-gold))] transition-colors"
                                        />
                                    </div>
                                    <div>
                                        <label className="tm-label block mb-2">{t.formMessage}</label>
                                        <textarea
                                            required
                                            rows={6}
                                            value={formData.message}
                                            onChange={e => setFormData({...formData, message: e.target.value})}
                                            className="w-full px-5 py-4 bg-[hsl(var(--tm-ivory-deep))] border border-[hsl(var(--tm-hairline))] text-[hsl(var(--tm-anthracite))] tm-serif text-lg focus:outline-none focus:border-[hsl(var(--tm-gold))] transition-colors resize-none"
                                        />
                                    </div>
                                    <button onClick={handleSubmit} className="tm-btn-primary">{t.formSubmit}</button>
                                </div>
                            )}
                        </Reveal>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
